import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "flowbite-react";
import {
  cloneLastSet,
  moveExercise,
  removeExercise,
} from "../../libs/workout/slice";
import { ExerciseSetFromServer } from "../../libs/workout/types";
import { useAppDispatch } from "../../store";
import { DeleteButton } from "../Button";
import { showReps, showWeight } from "../WorkoutTemplateTables";
import { getTonnage } from "../../libs/exercises/utils";
import { SaveAsTemplateModal } from "./SaveAsTemplateModal";
import { useIsCheckExercise } from "../../hooks/useIsCheckExercise";
import { DISTRICT_TO_MACRO_GROUP, MACRO_GROUP_COLORS } from "./muscleConfig";

type ExerciseRowProps = {
  dayIndex: number;
  exercise: {
    name: string;
    specialCategoryId?: number;
    muscleDistribution?: { district: string; percentage: number }[];
    weeks: {
      sets: ExerciseSetFromServer[] | undefined;
      notes: string | undefined;
      isCompleted: boolean | undefined;
    }[];
  };
  index: number;
  setOpenSetModal: (weekIndex: number) => void;
  setOpenExerciseModal?: (exerciseIndex: number) => void;
  isTemplate?: boolean;
  isEditMode?: boolean;
  isLastRow: boolean;
  isLastDay: boolean;
};
export const ExerciseRow: React.FC<ExerciseRowProps> = ({
  dayIndex,
  exercise,
  index,
  setOpenSetModal,
  setOpenExerciseModal,
  isTemplate,
  isEditMode,
  isLastRow,
  isLastDay,
}) => {
  const dispatch = useAppDispatch();
  const isCheck = useIsCheckExercise(exercise.specialCategoryId);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);
  // Calcola colore gradiente dal gruppo muscolare primario
  const gradientColor = useMemo(() => {
    const md = exercise.muscleDistribution;
    if (!md || md.length === 0) return null;
    // Trova il distretto con percentuale più alta
    const primary = md.reduce((best, curr) =>
      curr.percentage > best.percentage ? curr : best
    );
    const macroGroup = DISTRICT_TO_MACRO_GROUP[primary.district];
    return macroGroup ? MACRO_GROUP_COLORS[macroGroup] : null;
  }, [exercise.muscleDistribution]);

  // Numero massimo di set tra tutte le settimane per pareggiare l'altezza delle card
  const maxSets = Math.max(...exercise.weeks.map((w) => w.sets?.length ?? 0), 0);
  // Colonne uniformi: se almeno una settimana è completata, tutte mostrano la colonna KG
  const anyWeekCompleted = !isCheck && exercise.weeks.some((w) => w.isCompleted && w.sets && w.sets.length > 0);
  // Se almeno una settimana ha tonnellaggio, riserva lo spazio in tutte
  const anyHasTonnage = anyWeekCompleted;
  // Se almeno una settimana ha note, riserva lo spazio in tutte
  const anyHasNotes = exercise.weeks.some((w) => w.notes && w.sets && w.sets.length > 0);


  // template string example: "day-0-exercise-0"
  const id = `day-${dayIndex}-exercise-${index}`;
  return (
    <>
    <tr
      className="border-b border-gray-100 dark:border-gray-700"
      style={{
        background: gradientColor
          ? `linear-gradient(to right, ${gradientColor}80, ${gradientColor}30 30%, transparent 60%)`
          : undefined,
      }}
    >
      {isEditMode && (
        <th
          scope="row"
          className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
        >
          <div className="flex flex-row items-center gap-1">
            {/* Frecce su/giù */}
            <div className="inline-flex flex-col">
              {(index > 0 || dayIndex > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    const toExerciseIndex = index > 0 ? index - 1 : null;
                    const toDayIndex = index > 0 ? dayIndex : dayIndex - 1;
                    dispatch(
                      moveExercise({
                        dayIndex,
                        exerciseIndex: index,
                        toExerciseIndex,
                        toDayIndex,
                      })
                    );
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                >
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 14 8">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7 7.674 1.3a.91.91 0 0 0-1.348 0L1 7" />
                  </svg>
                </button>
              )}
              {!(isLastRow && isLastDay) && (
                <button
                  type="button"
                  onClick={() => {
                    const toExerciseIndex = isLastRow ? 0 : index + 1;
                    const toDayIndex = isLastRow ? dayIndex + 1 : dayIndex;
                    dispatch(
                      moveExercise({
                        dayIndex,
                        exerciseIndex: index,
                        toExerciseIndex,
                        toDayIndex,
                      })
                    );
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                >
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 14 8">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 5.326 5.7a.909.909 0 0 0 1.348 0L13 1" />
                  </svg>
                </button>
              )}
            </div>

            {/* Delete */}
            <DeleteButton
              variant="icon"
              onClick={() =>
                dispatch(removeExercise({ dayIndex, exerciseIndex: index }))
              }
            />

            {/* Menu ⋮ */}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 4 20">
                  <circle cx="2" cy="2" r="2" />
                  <circle cx="2" cy="10" r="2" />
                  <circle cx="2" cy="18" r="2" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute left-0 top-8 z-30 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-600 min-w-[170px] py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setOpenExerciseModal && setOpenExerciseModal(index);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 9V4.13a2.96 2.96 0 0 0-1.293.749L.879 7.707A2.96 2.96 0 0 0 .13 9H5Zm11.066-9H9.829a2.98 2.98 0 0 0-2.122.879L7 1.584A.987.987 0 0 0 6.766 2h4.3A3.972 3.972 0 0 1 15 6v10h1.066A1.97 1.97 0 0 0 18 14V2a1.97 1.97 0 0 0-1.934-2Z" />
                      <path d="M11.066 4H7v5a2 2 0 0 1-2 2H0v7a1.969 1.969 0 0 0 1.933 2h9.133A1.97 1.97 0 0 0 13 18V6a1.97 1.97 0 0 0-1.934-2Z" />
                    </svg>
                    Clona esercizio
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setSaveAsTemplateOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    Salva come template
                  </button>
                </div>
              )}
            </div>
          </div>
        </th>
      )}
      <th
        scope="row"
        className="px-5 py-3 text-center font-semibold text-gray-800 dark:text-gray-200 text-sm"
      >
        {exercise.name}
      </th>
      {exercise.weeks.map((week, i) => (
        <td
          key={i + "-ex-cell-week" + dayIndex}
          className={`px-3 py-3 ${(!week.sets || week.sets.length === 0) ? "align-middle text-center" : "align-top"}`}
          style={{ height: 1 }}
        >
          {week.sets && week.sets?.length > 0 && (
            <div className="flex flex-col h-full">
              {!isTemplate && (
                <div className="px-2 pb-1 min-h-[25px]">
                  {week.isCompleted ? <Checkmark /> : ""}
                </div>
              )}
              <button
                onClick={() => setOpenSetModal(i)}
                type="button"
                className="flex-1 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 rounded-xl text-[0.65rem] px-3 py-2 text-gray-700 dark:text-gray-300 transition-colors"
              >
                <Tooltip
                  className="p-2 text-xs"
                  theme={{ target: "" }}
                  content={`${exercise.name} / DAY ${dayIndex + 1} / WEEK ${
                    i + 1
                  }`}
                >
                  <table className="w-full text-center" style={{ tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th className={`${isCheck ? "w-[40%]" : "w-[25%]"} text-left`}>{isCheck ? "#" : "SET"}</th>
                        <th className={isCheck ? "w-[60%]" : "w-[30%]"}>{isCheck ? "VOTO" : "REPS"}</th>
                        {!isCheck && <th className="w-[20%]">REST</th>}
                        {anyWeekCompleted && <th className="w-[25%] text-right">KG</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {week.sets.map((set, inx) => (
                        <tr
                          key={inx + "" + set.rest + "" + inx + "" + dayIndex}
                          className={set.topSet ? "text-yellow-400 font-semibold" : ""}
                        >
                          <td className="text-left">{inx + 1} º{set.topSet ? " ★" : ""}</td>
                          <td>
                            {set.repsDone != null ? (
                              <span className="flex flex-col items-center leading-tight">
                                <span className="line-through text-gray-400 text-[0.55rem]">{showReps(set.reps, set.repsRange)}</span>
                                <span className="text-red-500 font-semibold">{showReps(set.repsDone, undefined)}</span>
                              </span>
                            ) : (
                              showReps(set.reps, set.repsRange)
                            )}
                          </td>
                          {!isCheck && <td>{set.rest}</td>}
                          {anyWeekCompleted && (
                            <td className="text-right text-red-500 font-semibold">
                              {week.isCompleted && set.weight ? showWeight(set.weight) : "—"}
                            </td>
                          )}
                        </tr>
                      ))}
                      {/* Righe vuote per pareggiare l'altezza tra le settimane */}
                      {Array.from({ length: maxSets - (week.sets?.length ?? 0) }).map((_, idx) => (
                        <tr key={`pad-${idx}`} className="invisible">
                          <td>&nbsp;</td>
                          <td>&nbsp;</td>
                          {!isCheck && <td>&nbsp;</td>}
                          {anyWeekCompleted && <td>&nbsp;</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Tooltip>
              </button>
              {anyHasTonnage && (
                <div className="px-1 pt-2 pb-1 text-[0.65rem] text-red-400 font-medium">
                  {week.isCompleted ? (
                    <>
                      <span>Tonnellaggio: </span>
                      <Tonnage sets={week.sets} />
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
              {anyHasNotes && (
                <div className="px-1 pt-1 pb-0 line-clamp-2 text-[0.6rem] text-gray-400 min-h-[1.5em]">
                  {week.notes ? (
                    <Tooltip className="p-2 text-xs" content={week.notes}>
                      {week.notes}
                    </Tooltip>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </div>
          )}
          {!isEditMode && (!week.sets || week.sets.length === 0) && (
            <div className="w-full flex justify-center items-center py-3">
              <span className="text-gray-300 text-base select-none">—</span>
            </div>
          )}
          {isEditMode && i > 0 && (!week.sets || !(week.sets?.length > 0)) && (
            <button
              onClick={() =>
                dispatch(
                  cloneLastSet({
                    weekIndex: i,
                    dayIndex: dayIndex,
                    exerciseIndex: index,
                  })
                )
              }
              type="button"
              className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium rounded-lg text-[0.6rem] px-2 py-1.5 text-center transition-colors"
            >
              CLONA SET PRECEDENTE
            </button>
          )}
          {isEditMode && (!week.sets || !(week.sets?.length > 0)) && (
            <button
              onClick={() => setOpenSetModal(i)}
              type="button"
              className="mt-4 bg-gray-800 border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-200 font-medium rounded-lg text-[0.6rem] px-2 py-1.5 text-center transition-colors"
            >
              APRI
            </button>
          )}
        </td>
      ))}
    </tr>
    {createPortal(
      <SaveAsTemplateModal
        openModal={saveAsTemplateOpen}
        setOpenModal={setSaveAsTemplateOpen}
        exerciseName={exercise.name}
        weeks={exercise.weeks}
      />,
      document.body
    )}
    </>
  );
};

const Checkmark = () => {
  return (
    <svg
      aria-hidden="true"
      className="w-5 h-5 text-blue-600 lg:w-6 lg:h-6 dark:text-blue-300 m-auto"
      fill="currentColor"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

//@ts-ignore
const Tonnage: React.FC<{ sets: ExerciseSetFromServer[] }> = ({ sets }) => {
  const tonnage = getTonnage(sets);

  if (typeof tonnage === "number") {
    return <span> {tonnage} kg</span>;
  }
  if (tonnage.length > 0) {
    return tonnage.map((tonnage, index) => (
      <div
        key={index}
        className="flex flex-row justify-between text-left items-center py-1"
      >
        <span>{tonnage.name}</span>
        <span>{tonnage.value}kg</span>
      </div>
    ));
  }
};
